<template>
  <section id="branches" class="section-padding bg-white fade-in-up">
    <div class="container">
      <div class="branch-module-container">
        <div class="text-center mb-4">
          <h2 class="section-title">分支机构</h2>
          <div class="section-divider w-20 mx-auto"></div>

          <!-- 分支机构选择导航 - 居中显示 -->
          <div class="branch-tabs-centered">
            <div
              v-for="(category, index) in branchCategories"
              :key="index"
              class="branch-tab"
              :class="{ active: activeBranch === index }"
              @click="selectBranch(index)"
            >
              <i class="fa fa-building mr-2"></i>{{ category.secondLevelModule }}
            </div>
          </div>
        </div>
        <div class="grid grid-cols-1 lg:branch-layout gap-8">
          <!-- 左侧Tab导航 - 文章类型选择 -->
          <div class="branch-sidebar">
            <div class="card-base p-4 border border-blue-500">
              <div class="branch-nav space-y-0">
                <div
                  v-for="(article, index) in currentArticles"
                  :key="index"
                  class="branch-nav-item p-4 border-b border-gray-200"
                  :class="{ active: activeTab === index }"
                  @click="selectTab(index)"
                >
                  <span>{{ article.title }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- 右侧内容区域 -->
          <div class="branch-main">
            <!-- Tab内容区域 -->
            <div class="branch-content-wrapper">
              <div v-if="loading" class="loading-text">加载中...</div>
              <div v-else-if="currentArticles.length === 0" class="empty-text">暂无相关文章</div>
              <div v-else class="branch-content" v-html="currentArticleContent"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { api } from '../api'

export default {
  name: 'BranchSection',
  data() {
    return {
      activeBranch: 0,
      activeTab: 0,
      branchCategories: [],
      branchArticles: {},
      articleDetails: {},
      loading: false
    }
  },
  computed: {
    currentArticles() {
      return this.branchArticles[this.activeBranch] || []
    },
    currentArticleContent() {
      const article = this.currentArticles[this.activeTab]
      if (!article) return ''
      return this.articleDetails[article.id] || article.summary || '暂无内容'
    }
  },
  async mounted() {
    await this.loadBranchCategories()
  },
  watch: {
    activeBranch() {
      this.activeTab = 0
      this.loadBranchArticles()
    },
    activeTab() {
      this.loadArticleDetail()
    }
  },
  methods: {
    async loadBranchCategories() {
      try {
        this.loading = true
        const categories = await api.getCategories()
        const branchCategories = categories.filter(cat => cat.firstLevelModule === '分支机构')
        this.branchCategories = branchCategories
        if (branchCategories.length > 0) {
          await this.loadBranchArticles()
        }
      } catch (error) {
        console.error('加载分支机构分类失败:', error)
      } finally {
        this.loading = false
      }
    },
    async loadBranchArticles() {
      if (this.branchCategories.length === 0 || this.activeBranch >= this.branchCategories.length) return

      try {
        const categoryId = this.branchCategories[this.activeBranch].id
        const result = await api.getArticles(categoryId)
        this.branchArticles[this.activeBranch] = result.rows.slice(0, 3).map(article => ({
          id: article.id,
          title: article.title || '暂无标题',
          summary: article.summary || ''
        }))

        const titleOrder = ['机构简介', '理事会结构', '机构章程']
        this.branchArticles[this.activeBranch].sort((a, b) => {
          const indexA = titleOrder.indexOf(a.title)
          const indexB = titleOrder.indexOf(b.title)
          if (indexA === -1 && indexB === -1) return 0
          if (indexA === -1) return 1
          if (indexB === -1) return -1
          return indexA - indexB
        })

        if (this.branchArticles[this.activeBranch].length > 0) {
          await this.loadArticleDetail()
        }
      } catch (error) {
        console.error('加载分支机构文章失败:', error)
        this.branchArticles[this.activeBranch] = []
      }
    },
    async loadArticleDetail() {
      const article = this.currentArticles[this.activeTab]
      if (!article) return

      if (this.articleDetails[article.id]) return

      try {
        const detail = await api.getArticleDetail(article.id)
        this.articleDetails[article.id] = detail.contentHtml || detail.content || detail.summary || '暂无内容'
      } catch (error) {
        console.error('加载文章详情失败:', error)
        this.articleDetails[article.id] = article.summary || '暂无内容'
      }
    },
    selectBranch(index) {
      this.activeBranch = index
    },
    selectTab(index) {
      this.activeTab = index
    }
  }
}
</script>

<style scoped>
.section-padding {
  background-color: var(--bg);
  position: relative;
  overflow: hidden;
  padding: 16px 0;
}

.branch-module-container{
  background: #ffffff;
  border-radius: 32px;
  box-shadow: 0 20px 40px -15px rgba(37,99,235,0.08);
  padding: 48px 40px;
  margin-bottom: 0;
  border: 1px solid rgba(37,99,235,0.12);
}

.section-padding::before{
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: radial-gradient(circle at 70% 30%, rgba(96, 165, 250, 0.1) 0%, transparent 60%);
  pointer-events: none;
  z-index: 0;
}

.container{
  position: relative;
  z-index: 1;
}

.section-divider{
  height: 2px;
  background: linear-gradient(90deg, transparent, var(--accent), transparent);
  margin: 1rem auto;
  box-shadow: 0 0 10px rgba(96, 165, 250, 0.5);
}

/* Tab导航居中样式 */
.branch-tabs-centered{
  display: flex;
  justify-content: center;
  gap: 16px;
  margin-top: 24px;
  flex-wrap: wrap;
}

.branch-tab{
  padding: 12px 28px;
  cursor: pointer;
  border-radius: 24px;
  background: var(--surface);
  color: var(--text-secondary);
  font-weight: 500;
  font-size: 15px;
  transition: all 0.3s ease;
  white-space: nowrap;
  border: 1px solid var(--border);
}

.branch-tab:hover{
  background: rgba(96, 165, 250, 0.1);
  color: var(--accent);
  border-color: rgba(96, 165, 250, 0.3);
}

.branch-tab.active{
  background: rgba(96, 165, 250, 0.15);
  color: var(--accent);
  border-color: var(--accent);
  box-shadow: 0 0 15px rgba(96, 165, 250, 0.3);
}

.grid-cols-1{
  display: grid;
  grid-template-columns: 1fr;
  height: auto;
  min-height: 450px;
  gap: 40px;
  align-items: stretch;
}

@media (min-width: 1024px) {
  .lg\:branch-layout {
    grid-template-columns: auto 1fr;
  }
}

.branch-sidebar{
  display: flex;
  flex-direction: column;
  height: 100%;
}

.branch-sidebar .card-base{
  height: 100%;
  display: flex;
  flex-direction: column;
  background: var(--surface);
  backdrop-filter: blur(20px) saturate(120%);
  -webkit-backdrop-filter: blur(20px) saturate(120%);
  border-radius: var(--radius-2xl);
  box-shadow: var(--shadow);
  border: 1px solid var(--border);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.branch-sidebar .card-base:hover{
  /*transform: translateY(-8px) scale(1.015);*/
  box-shadow: 0 15px 35px -10px rgba(37, 99, 235, 0.15), 0 0 20px rgba(96, 165, 250, 0.2);
  border-color: rgba(96, 165, 250, 0.3);
}

.branch-nav{
  display: flex;
  flex-direction: column;
  height: 100%;
  flex: 1;
}

.branch-nav-item{
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  border-bottom: 1px solid var(--border);
  color: var(--text-secondary);
  position: relative;
  overflow: hidden;
  flex: 1;
  min-height: 100px;
  white-space: nowrap;
}

.branch-nav-item::before{
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(96, 165, 250, 0.1), transparent);
  transition: left 0.3s ease;
}

.branch-nav-item:hover::before{
  left: 100%;
}

.branch-nav-item:last-child{
  border-bottom: none;
}

.branch-nav-item:hover{
  background-color: rgba(96, 165, 250, 0.1);
  color: var(--accent);
}

.branch-nav-item.active{
  background-color: rgba(96, 165, 250, 0.15);
  color: var(--accent);
  border-left: 4px solid var(--accent);
  box-shadow: 0 0 15px rgba(96, 165, 250, 0.3);
}

.branch-nav-item i{
  color: var(--accent);
  margin-right: 12px;
  transition: transform 0.3s ease;
}

.branch-nav-item:hover i{
  /*transform: scale(1.1);*/
}

.branch-main{
  display: flex;
  flex-direction: column;
  height: 100%;
}

.branch-content-wrapper{
  background: var(--surface);
  border-radius: var(--radius-2xl);
  box-shadow: var(--shadow);
  border: 1px solid var(--border);
  padding: 24px;
  overflow-y: auto;
  height: 450px;
}

.branch-content-wrapper::-webkit-scrollbar{
  width: 6px;
}

.branch-content-wrapper::-webkit-scrollbar-track{
  background: rgba(96, 165, 250, 0.05);
  border-radius: 3px;
}

.branch-content-wrapper::-webkit-scrollbar-thumb{
  background: rgba(96, 165, 250, 0.3);
  border-radius: 3px;
}

.branch-content-wrapper::-webkit-scrollbar-thumb:hover{
  background: rgba(96, 165, 250, 0.5);
}

.branch-content{
  color: var(--text-primary);
  line-height: 1.8;
  font-size: 14px;
}

.branch-content :first-child{
  margin-top: 0;
}

.branch-content :last-child{
  margin-bottom: 0;
}

.loading-text,
.empty-text{
  text-align: center;
  padding: 60px 20px;
  color: var(--text-secondary);
  font-size: 14px;
}

@media (max-width: 1024px){
  .branch-nav-item{
    padding: 12px 8px;
    font-size: 14px;
  }

  .branch-tabs-centered{
    gap: 12px;
  }

  .branch-tab{
    padding: 10px 20px;
    font-size: 14px;
  }
}

@media (max-width: 768px){
  .section-padding{
    padding: 48px 0;
  }

  .branch-nav-item{
    padding: 12px 16px;
    white-space: normal;
  }

  .grid-cols-1{
    gap: 24px;
  }

  .branch-tabs-centered{
    gap: 8px;
  }

  .branch-tab{
    padding: 8px 16px;
    font-size: 13px;
  }

  .branch-content-wrapper{
    height: 300px;
    max-height: 40vh;
  }
}
</style>
